/**
 * ResultsTable Component
 *
 * Displays table of results with:
 * - Job Input ID, Status Badge, Critical Keys, Duration, Timestamp, Error
 * - View Session link (opens in new tab)
 */

import React from 'react';
import { Result } from '../../types/result';
import { StatusBadge } from '../common/StatusBadge';

interface ResultsTableProps {
  results: Result[];
  criticalKeys: string[];
  onErrorClick?: (errorMessage: string) => void;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  criticalKeys,
  onErrorClick
}) => {

  const getNestedValue = (obj: any, keyPath: string): any => {
    if (!obj || !keyPath) return null;

    const keys = keyPath.split('.');
    let value = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  };

  const formatCriticalKeyValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatDuration = (duration: number | null): string => {
    if (duration === null || duration === undefined) {
      return 'N/A';
    }
    if (duration < 60) {
      return `${duration.toFixed(1)}s`;
    }
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}m ${seconds}s`;
  };

  const formatTimestamp = (timestamp: number): string => {
    const now = Date.now();
    const date = new Date(timestamp * 1000); // Convert epoch seconds to milliseconds
    const diffMs = now - (timestamp * 1000);
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Relative time for recent timestamps
    if (diffSecs < 60) {
      return `${diffSecs}s ago`;
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    }

    // Absolute date for older timestamps (in local time)
    return date.toLocaleString();
  };

  const truncateInputId = (inputId: string | null | undefined, maxLength: number = 20): string => {
    if (!inputId) return '-';
    if (inputId.length <= maxLength) return inputId;
    return inputId.substring(0, maxLength) + '...';
  };

  const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Job Input ID
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              {/* Dynamic columns for critical keys */}
              {criticalKeys.map((key) => (
                <th
                  key={key}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  title={`Critical key: ${key}`}
                >
                  {key}
                </th>
              ))}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Duration
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Timestamp
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Error
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {results.map((result) => (
              <tr
                key={result.id}
                className="hover:bg-gray-50 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <a
                    href={`/jobs/${result.job_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    title={result.input_id || 'No input ID'}
                  >
                    {truncateInputId(result.input_id)}
                  </a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={result.status} />
                </td>
                {/* Dynamic cells for critical keys */}
                {criticalKeys.map((key) => {
                  const value = getNestedValue(result.result, key);
                  const hasValue = value !== null && value !== undefined && value !== '';
                  const formattedValue = formatCriticalKeyValue(value);
                  return (
                    <td
                      key={key}
                      className="px-6 py-4 text-sm text-gray-500 max-w-xs"
                      title={`${key}: ${formattedValue}`}
                    >
                      <div className={`truncate ${hasValue ? 'text-gray-900' : 'text-gray-400'}`}>
                        {truncateText(formattedValue)}
                      </div>
                    </td>
                  );
                })}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDuration(result.duration)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span title={new Date(result.timestamp * 1000).toLocaleString()}>
                    {formatTimestamp(result.timestamp)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                  {result.error ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onErrorClick?.(result.error!);
                      }}
                      className="text-red-600 hover:text-red-800 hover:underline truncate block text-left cursor-pointer"
                      title={`Click to filter by: ${result.error}`}
                    >
                      {result.error}
                    </button>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {result.session_id ? (
                    <a
                      href={`/sessions/${result.session_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                      aria-label="View session"
                    >
                      View Session
                    </a>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
